import { v7 as uuidv7 } from 'uuid';
import { getDexieStore } from './dexie.js';
import type { CachedShot, OutboxEntry, OutboxOp, SyncStore } from './store.js';
import { withSyncLease } from './lease.js';

export type Submitter = (
  entry: OutboxEntry,
  signal: AbortSignal,
) => Promise<{ ok: true } | { ok: false; retry: boolean; error: string; pause?: boolean }>;

export interface OutboxOptions {
  submit: Submitter;
  maxAttempts?: number;
  baseDelayMs?: number;
  onChange?: (entry: OutboxEntry) => void;
  /**
   * Backend persistence. Optional: defaults (lazily) to the Dexie/IndexedDB
   * store for the web PWA. The native app passes its SQLite-backed SyncStore.
   * The default is resolved lazily so importing this module in a non-browser
   * (React Native) runtime never constructs Dexie.
   */
  store?: SyncStore;
}

export async function enqueue(
  op: OutboxOp,
  payload: unknown,
  store: SyncStore = getDexieStore(),
): Promise<OutboxEntry> {
  const entry: OutboxEntry = {
    id: uuidv7(),
    op,
    payload,
    createdAt: Date.now(),
    attempts: 0,
    status: 'pending',
  };
  await store.addOutbox(entry);
  return entry;
}

export async function pending(store: SyncStore = getDexieStore()): Promise<OutboxEntry[]> {
  return store.listPendingAndInflight();
}

export class OutboxDrainer {
  private running: Promise<void> | null = null;
  private requested = false;
  private aborter: AbortController | null = null;
  private readonly maxAttempts: number;
  private readonly baseDelayMs: number;
  private readonly store: SyncStore;

  constructor(private readonly opts: OutboxOptions) {
    this.maxAttempts = opts.maxAttempts ?? 8;
    this.baseDelayMs = opts.baseDelayMs ?? 500;
    this.store = opts.store ?? getDexieStore();
  }

  drain(): Promise<void> {
    this.requested = true;
    if (this.running) return this.running;
    this.aborter = new AbortController();
    const signal = this.aborter.signal;
    this.running = (async () => {
      do {
        this.requested = false;
        await this.run(signal);
      } while (this.requested && !signal.aborted);
    })().finally(() => {
      this.running = null;
      this.aborter = null;
    });
    return this.running;
  }

  private async run(cancelSignal: AbortSignal): Promise<void> {
    await withSyncLease(
      this.store,
      async (signal, assertOwned) => {
        // Only the lease owner may recover an interrupted upload.
        await this.store.transaction(async (tx) => {
          await assertOwned(tx);
          for (const entry of await tx.listPendingAndInflight()) {
            if (entry.status === 'inflight') await tx.updateOutbox(entry.id, { status: 'pending' });
          }
        });
        while (!signal.aborted) {
          const next = await this.store.transaction(async (tx) => {
            await assertOwned(tx);
            const blocked = new Set((await tx.listFailedTerminal()).map(assignmentOf));
            const entry = (await tx.listPendingAndInflight()).find(
              (e) => e.status === 'pending' && !blocked.has(assignmentOf(e)),
            );
            if (!entry) return undefined;
            const claimed = { ...entry, status: 'inflight' as const, attempts: entry.attempts + 1 };
            await tx.updateOutbox(entry.id, claimed);
            return claimed;
          });
          if (!next) return;
          let result: Awaited<ReturnType<Submitter>>;
          try {
            result = await this.opts.submit(next, signal);
          } catch (err) {
            result = { ok: false, retry: true, error: String(err) };
          }
          const status = result.ok
            ? 'acked'
            : signal.aborted || result.pause
              ? 'pending'
              : !result.retry || next.attempts >= this.maxAttempts
                ? 'failed_terminal'
                : 'pending';
          await this.store.transaction(async (tx) => {
            // On cancellation leave inflight persisted; the next owner recovers it.
            await assertOwned(tx);
            await tx.updateOutbox(next.id, {
              status,
              ...(!result.ok ? { lastError: result.error } : {}),
              ...(!result.ok && result.pause ? { attempts: next.attempts - 1 } : {}),
            });
          });
          this.opts.onChange?.({ ...next, status });
          if (!result.ok && result.pause) return;
          if (status === 'pending') await sleep(this.backoff(next.attempts));
        }
      },
      cancelSignal,
    );
  }

  cancel() {
    this.aborter?.abort();
  }

  private backoff(attempts: number): number {
    const exp = Math.min(this.baseDelayMs * 2 ** (attempts - 1), 30_000);
    return exp + Math.random() * exp * 0.25;
  }
}

export function assignmentOf(entry: OutboxEntry): string | undefined {
  return (entry.payload as { roundAssignmentId?: string } | null)?.roundAssignmentId;
}

export async function retryAssignment(
  assignmentId: string,
  store: SyncStore = getDexieStore(),
): Promise<void> {
  await store.transaction(async (tx) => {
    for (const entry of await tx.listFailedTerminal()) {
      if (assignmentOf(entry) === assignmentId)
        await tx.updateOutbox(entry.id, { status: 'pending' });
    }
  });
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * One shot row as reported by the server. zoneCode/timing are optional so
 * existing callers keep compiling; when a caller omits a field, reconcile
 * preserves the locally-cached value instead of blanking it.
 */
export interface ServerShotRow {
  ord: number;
  valueNumeric: number | null;
  clientIdempotencyKey: string;
  zoneCode?: string | null;
  firstShotTimeMs?: number | null;
  elapsedTimeMs?: number | null;
}

/**
 * Reconcile local cache with server truth for one round assignment.
 * - Replaces local shot cache with server truth, KEEPING local rows whose
 *   write is still queued (pending/inflight) in the outbox — wiping those
 *   would make freshly-entered offline shots vanish from the screen until
 *   the next reconcile and invite duplicate re-entry.
 * - Acks failed_terminal outbox entries whose payload actually landed on
 *   the server (visible via the idempotency key).
 *
 * Terminal entries that the server does NOT have are left alone. They were
 * rejected with a 4xx (round_locked, validation, etc.); retrying would just
 * loop. The UI retains them for explicit retry.
 */
export async function reconcileAssignment(
  assignmentId: string,
  fetchServerShots: (assignmentId: string) => Promise<ServerShotRow[]>,
  store: SyncStore = getDexieStore(),
  options: { discardQueued?: boolean } = {},
): Promise<{ kept: number; acked: number }> {
  // Capture intent before waiting for the lease/network. New edits (including
  // coalesced changes to an existing entry) must survive a slow discard.
  const discardSnapshot = options.discardQueued
    ? await store.transaction(
        async (tx) =>
          new Map(
            [...(await tx.listPendingAndInflight()), ...(await tx.listFailedTerminal())]
              .filter((e) => assignmentOf(e) === assignmentId)
              .map((e) => [e.id, JSON.stringify(e.payload)]),
          ),
      )
    : undefined;
  return withSyncLease(store, async (_signal, assertOwned) => {
    const server = await fetchServerShots(assignmentId);
    return store.transaction(async (tx) => {
      await assertOwned(tx);
      store = tx;
      const serverKeys = new Set(server.map((s) => s.clientIdempotencyKey));
      const local = await store.shotsForAssignment(assignmentId);
      const localByKey = new Map(local.map((s) => [s.clientIdempotencyKey, s]));

      // Replace local cache with server truth (server is authoritative), but
      // fall back to the cached row for fields the caller didn't fetch.
      const rows: CachedShot[] = server.map((s) => {
        const prev = localByKey.get(s.clientIdempotencyKey);
        return {
          id: s.clientIdempotencyKey,
          roundAssignmentId: assignmentId,
          ord: s.ord,
          valueNumeric: s.valueNumeric,
          zoneCode: s.zoneCode !== undefined ? s.zoneCode : (prev?.zoneCode ?? null),
          firstShotTimeMs:
            s.firstShotTimeMs !== undefined ? s.firstShotTimeMs : (prev?.firstShotTimeMs ?? null),
          elapsedTimeMs:
            s.elapsedTimeMs !== undefined ? s.elapsedTimeMs : (prev?.elapsedTimeMs ?? null),
          clientIdempotencyKey: s.clientIdempotencyKey,
          recordedAt: prev?.recordedAt ?? Date.now(),
          ...(prev?.source ? { source: prev.source } : {}),
        };
      });

      // Keep optimistic local rows that are still on their way to the server.
      let unresolved = [
        ...(await store.listPendingAndInflight()),
        ...(await store.listFailedTerminal()),
      ]
        .filter((e) => !assignmentOf(e) || assignmentOf(e) === assignmentId)
        .sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
      if (discardSnapshot) {
        const changedKeys = new Set(
          unresolved
            .filter((e) => discardSnapshot.get(e.id) !== JSON.stringify(e.payload))
            .map((e) => (e.payload as Partial<CachedShot>).clientIdempotencyKey),
        );
        const kept: OutboxEntry[] = [];
        for (const entry of unresolved) {
          const key = (entry.payload as Partial<CachedShot>).clientIdempotencyKey;
          if (discardSnapshot.has(entry.id) && !changedKeys.has(key))
            await store.updateOutbox(entry.id, { status: 'acked' });
          else kept.push(entry);
        }
        unresolved = kept;
      }
      let acked = 0;
      const serverByKey = new Map(server.map((s) => [s.clientIdempotencyKey, s]));
      const blockedKeys = new Set<string>();
      const stillUnresolved: OutboxEntry[] = [];
      for (const entry of unresolved) {
        const payload = entry.payload as Partial<ServerShotRow>;
        const key = payload.clientIdempotencyKey;
        const row = key ? serverByKey.get(key) : undefined;
        const fields = Object.entries(payload).filter(
          ([name]) => name !== 'roundAssignmentId' && name !== 'clientIdempotencyKey',
        );
        const landed =
          key &&
          !blockedKeys.has(key) &&
          ((entry.op === 'shot.add' && !!row) ||
            (entry.attempts > 0 && entry.op === 'shot.remove' && !row) ||
            (entry.attempts > 0 &&
              entry.op === 'shot.update' &&
              row &&
              fields.length > 0 &&
              fields.every(
                ([name, value]) =>
                  Object.hasOwn(row, name) && row[name as keyof ServerShotRow] === value,
              )));
        if (landed) {
          await store.updateOutbox(entry.id, { status: 'acked' });
          acked++;
        } else {
          if (key) blockedKeys.add(key);
          stillUnresolved.push(entry);
        }
      }
      unresolved = stillUnresolved;
      const queuedKeys = new Set(
        unresolved
          .filter((e) => e.op === 'shot.add')
          .map(
            (e) =>
              (e.payload as { clientIdempotencyKey?: string } | undefined)?.clientIdempotencyKey,
          )
          .filter((k): k is string => typeof k === 'string'),
      );
      for (const shot of local) {
        if (
          !serverKeys.has(shot.clientIdempotencyKey) &&
          queuedKeys.has(shot.clientIdempotencyKey)
        ) {
          rows.push(shot);
        }
      }

      // Preserve optimistic corrections; a stale server read must not resurrect a delete.
      const merged = new Map(rows.map((s) => [s.clientIdempotencyKey, s]));
      for (const entry of unresolved) {
        const payload = entry.payload as Partial<CachedShot>;
        const key = payload.clientIdempotencyKey;
        if (!key) continue;
        if (entry.op === 'shot.remove') merged.delete(key);
        if (entry.op === 'shot.update') {
          const row = merged.get(key);
          if (row) merged.set(key, { ...row, ...payload });
        }
      }
      await store.replaceShotsForAssignment(assignmentId, [...merged.values()]);

      return { kept: local.length, acked };
    });
  });
}

export function attachVisibilityDrainer(drainer: OutboxDrainer): () => void {
  if (typeof document === 'undefined') return () => {};
  const handler = () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      void drainer.drain().catch((err) => console.error('sync failed', err));
    }
  };
  document.addEventListener('visibilitychange', handler);
  window.addEventListener('online', handler);
  return () => {
    document.removeEventListener('visibilitychange', handler);
    window.removeEventListener('online', handler);
  };
}
